import { useState } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import EditIcon from '@mui/icons-material/Edit';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Select,
  TextField,
  Stack,
  CircularProgress
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import authService from '../../../api-authorization/AuthorizeService';

const UpdateDeviceDialog = ({
  client,
  tractor,
  device,
  unaffiliatedDevices,
  open,
  onClose,
  onSuccess
}) => {
  const [loading, setLoading] = useState(false);
  const [baudLoading, setBaudLoading] = useState(false);
  const { handleSubmit, register, watch } = useForm({ defaultValues: device });
  const serialNumber = watch('serialNumber', device.serialNumber);
  const installLocation = watch('installLocation', device.installLocation);
  const flowPulsesPerGal1 = watch('flowPulsesPerGal1', device.flowPulsesPerGal1);  
  const flowPulsesPerGal2 = watch('flowPulsesPerGal2', device.flowPulsesPerGal2);  
  const flowPulsesPerGal3 = watch('flowPulsesPerGal3', device.flowPulsesPerGal3);
  const externalGpsBaudRate = watch('externalGpsBaudRate', device.externalGpsBaudRate);
  const isActive = watch('isActive', device.isActive);

  const navigate = useNavigate();

  const handleCalibrate = (flowMeterNumber) => {
    navigate(
      `/admin/device/${encodeURIComponent(serialNumber)}/flow-calibration?clientId=${client.id}&flowMeterNumber=${flowMeterNumber}`
    );
  };

  const updateDevice = async (data) => {
    if (!serialNumber) {
      return toast.error('Please select the device serial number');
    }    
    if (!flowPulsesPerGal1 || !flowPulsesPerGal2 || !flowPulsesPerGal3) {
      return toast.error(
        'Please enter a valid flow meter pulses / gal value for all flow meter fields.'
      );
    }

    const token = await authService.getAccessToken();
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/admin/devices/update`,
        {
          method: 'PUT',
          headers: !token
            ? {}
            : {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
          body: JSON.stringify({
            clientId: client.id,
            tractorId: tractor.id,
            ...data
          })
        }
      );

      const responseText = await response.text();

      if (responseText.includes('duplicate key')) {
        return toast.error('Device with this serial number already exists!');
      }

      if (response.ok) {
        toast.success('Device updated successfully!');
        onSuccess();
      } else {
        toast.error(`${response.status} ${response.statusText}`);
      }
    } catch (err) {
      toast.error('Error: ' + err);
      console.log('err', err);
    }
    setLoading(false);
  };

  const handleSetBaudRate = async () => {
    if (!externalGpsBaudRate) {
      toast.error('Please enter a valid baud rate.');
      return;
    }
    setBaudLoading(true);
    const token = await authService.getAccessToken();
    try {
      // POST to set baud rate
      const postResponse = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/diagnostics/set-baud-rate`,
        {
          method: 'POST',
          headers: !token
            ? {}
            : {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
          body: JSON.stringify({
            clientId: client.id,
            tractorId: tractor.id,
            baudRate: externalGpsBaudRate
          })
        }
      );
      const postResult = await postResponse.json();
      const { code } = postResult;
      if (!code) {
        toast.error('Failed to initiate baud rate change.');
        setBaudLoading(false);
        return;
      }

      // Poll GET endpoint
      const pollBaudRate = async () => {
        const getResponse = await fetch(
          `${process.env.REACT_APP_BASE_URL}/api/diagnostics/set-baud-rate?clientId=${client.id}&code=${code}`,
          {
            method: 'GET',
            headers: !token
              ? {}
              : {
                  Authorization: `Bearer ${token}`,
                  'Content-Type': 'application/json'
                }
          }
        );
        const getResult = await getResponse.json();
        if (getResult.completed) {
          const message =
            Array.isArray(getResult.data) && getResult.data.length > 0
              ? getResult.data[0].message
              : 'Baud rate has been successfully set!';
          toast.success(message);
          setBaudLoading(false);
        } else {
          setTimeout(pollBaudRate, 5000);
        }
      };
      pollBaudRate();
    } catch (err) {
      toast.error('Error: ' + err);
      setBaudLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <form onSubmit={handleSubmit(updateDevice)}>
        <DialogTitle>
          <EditIcon /> Edit Device
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Serial Number on the same line, both bold */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <span style={{ marginRight: 8 }}>Serial Number:</span>
              <span style={{ fontWeight: 600 }}>{serialNumber}</span>
            </Box>
            <FormControl>
              <FormLabel>Install Location</FormLabel>
              <TextField required {...register('installLocation')} sx={{ width: 300 }} />
            </FormControl>
            <FormControl>
              <FormLabel>Flow 1 Pulses / Gal</FormLabel>
              <Stack direction="row" spacing={1} alignItems="center">
                <TextField
                  required
                  type="number"
                  {...register('flowPulsesPerGal1')}
                  InputProps={{ inputProps: { step: 'any' } }}
                  sx={{ width: 300 }}
                />
                <Button
                  variant="outlined"
                  onClick={() => handleCalibrate(1)}
                  sx={{ whiteSpace: 'nowrap', width: 120 }}
                >
                  Calibrate
                </Button>
              </Stack>
            </FormControl>
            <FormControl>
              <FormLabel>Flow 2 Pulses / Gal</FormLabel>
              <Stack direction="row" spacing={1} alignItems="center">
                <TextField
                  required
                  type="number"
                  {...register('flowPulsesPerGal2')}
                  InputProps={{ inputProps: { step: 'any' } }}
                  sx={{ width: 300 }}
                />
                <Button
                  variant="outlined"
                  onClick={() => handleCalibrate(2)}
                  sx={{ whiteSpace: 'nowrap', width: 120 }}
                >
                  Calibrate
                </Button>
              </Stack>
            </FormControl>
            <FormControl>
              <FormLabel>Flow 3 Pulses / Gal</FormLabel>
              <Stack direction="row" spacing={1} alignItems="center">
                <TextField
                  required
                  type="number"
                  {...register('flowPulsesPerGal3')}
                  InputProps={{ inputProps: { step: 'any' } }}
                  sx={{ width: 300 }}
                />
                <Button
                  variant="outlined"
                  onClick={() => handleCalibrate(3)}
                  sx={{ whiteSpace: 'nowrap', width: 120 }}
                >
                  Calibrate
                </Button>
              </Stack>
            </FormControl>
            <FormControl>
              <FormLabel>External Gps Baud Rate</FormLabel>
              <Stack direction="row" spacing={1} alignItems="center">
                <TextField
                  type="number"
                  {...register('externalGpsBaudRate')}
                  InputProps={{ inputProps: { step: 'any' } }}
                  sx={{ width: 300 }}
                />
                <Button
                  variant="outlined"
                  onClick={handleSetBaudRate}
                  sx={{ whiteSpace: 'nowrap', width: 120 }}
                  disabled={baudLoading}
                >
                  Set
                </Button>
                {baudLoading && <CircularProgress size={24} sx={{ ml: 1 }} />}
              </Stack>
            </FormControl>
            <FormControlLabel
                control={
                    <Checkbox
                        checked={isActive}
                        {...register('isActive')}
                    />
                }
                label="Is Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={onClose} disabled={loading} sx={{ width: 150 }}>
            Cancel
          </Button>
          <Button type="submit" variant="outlined" disabled={loading} sx={{ width: 150 }}>
            Save Changes
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default UpdateDeviceDialog;
